import { Box, Button, Dialog, DialogContent, useMediaQuery } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDaphne } from "../hooks/useDaphne";
import { slugify } from "../../domain/url/utils";
import { theme } from "../layout/Theme";
import { useI18n } from "../hooks/useI18n";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

interface KnowledgeVaccineModalProps {
  mesvaccinsUrl: string;
}

export const KnowledgeVaccineModal = ({ mesvaccinsUrl }: KnowledgeVaccineModalProps) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const vaccineCodeParam = searchParams.get("vaccine-code");
  if (!vaccineCodeParam) throw new Error("Vaccine code is required");
  const vaccineCode = parseInt(vaccineCodeParam);
  const daphne = useDaphne();
  const vaccine = daphne.repositories.vaccines.all().find((v) => v.code === vaccineCode);
  if (!vaccine) throw new Error("Vaccine not found");
  const mesvaccinsEmbedUrl = buildMesvaccinsModalUrl(mesvaccinsUrl, vaccine.code, vaccine.name.fr ?? "");
  const mesvaccinsKnowledgeUrl = buildMesvaccinsKnowledgeUrl(vaccine.code, vaccine.name.fr ?? "");
  const modalFullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={true}
      onClose={(event: React.SyntheticEvent) => {
        event.stopPropagation();
        navigate("..");
      }}
      maxWidth="lg"
      fullWidth
      fullScreen={modalFullScreen}
    >
      <DialogContent
        sx={{
          pt: { xs: 0, md: 3 },
          px: { xs: 0, md: 3 },
          pb: { xs: 8, md: 3 },
          height: "80vh",
          position: "relative",
        }}
      >
        <iframe src={mesvaccinsEmbedUrl} width="100%" height="100%" frameBorder="0" style={{ display: "block" }} />

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            position: "absolute",
            bottom: "20px",
            right: "20px",
            gap: 1,
          }}
        >
          <Button fullWidth variant="outlined" onClick={() => navigate("..")}>
            {t("common.cta.close")}
          </Button>

          <Button
            fullWidth
            variant="contained"
            href={mesvaccinsKnowledgeUrl}
            target="_blank"
            endIcon={<OpenInNewIcon />}
          >
            {t("common.cta.open")}
          </Button>
        </Box>
      </DialogContent>

      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          p: 2,
          gap: 2,
          background: "#fff",
          borderTop: "1px solid #ddd",
        }}
      >
        <Button fullWidth variant="outlined" onClick={() => navigate("..")}>
          {t("common.cta.close")}
        </Button>

        <Button fullWidth variant="contained" href={mesvaccinsKnowledgeUrl} target="_blank" endIcon={<OpenInNewIcon />}>
          {t("common.cta.open")}
        </Button>
      </Box>
    </Dialog>
  );
};

function buildMesvaccinsModalUrl(mesvaccinsBaseUrl: string, vaccineCode: number, vaccineName: string): string {
  const vaccineSlug = slugify(vaccineName);
  const mesvaccinsEmbedUrl = `${mesvaccinsBaseUrl}/web/vaccines/${vaccineCode}-${vaccineSlug}-embed?navigation=false`;
  return mesvaccinsEmbedUrl;
}

function buildMesvaccinsKnowledgeUrl(vaccineCode: number, vaccineName: string): string {
  const vaccineSlug = slugify(vaccineName);
  const mesvaccinsEmbedUrl = `/knowledge/web/vaccines/${vaccineCode}-${vaccineSlug}-embed`;
  return mesvaccinsEmbedUrl;
}
